import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatWhatsApp, validateWhatsApp } from '@/utils/formValidation';
import { cn } from '@/lib/utils';

interface WhatsAppInputProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (maskedValue: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  validate?: boolean;
  showLabel?: boolean;
  onValidChange?: (isValid: boolean) => void;
}

/**
 * Campo padronizado de WhatsApp com máscara (XX) XXXXX-XXXX e validação BR.
 * Sempre devolve o valor já mascarado via onChange. Para gravar no banco,
 * use whatsapp.replace(/\D/g, '') no consumidor.
 */
export const WhatsAppInput: React.FC<WhatsAppInputProps> = ({
  id = 'whatsapp',
  name = 'whatsapp',
  value,
  onChange,
  label = 'WhatsApp',
  placeholder = '(11) 99999-9999',
  required,
  disabled,
  className,
  inputClassName,
  validate = true,
  showLabel = true,
  onValidChange,
}) => {
  const [error, setError] = useState<string | undefined>();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = formatWhatsApp(e.target.value);
    onChange(masked);
    const v = validateWhatsApp(masked);
    onValidChange?.(v.isValid);
    if (error && v.isValid) setError(undefined);
  };

  // Notify initial validity (for empty optional fields, etc.)
  React.useEffect(() => {
    if (!validate) {
      onValidChange?.(true);
      return;
    }
    if (!required && !value) {
      onValidChange?.(true);
      return;
    }
    onValidChange?.(validateWhatsApp(value).isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBlur = () => {
    if (!validate) return;
    if (!required && !value) {
      setError(undefined);
      onValidChange?.(true);
      return;
    }
    const v = validateWhatsApp(value);
    setError(v.isValid ? undefined : v.errors[0]);
    onValidChange?.(v.isValid);
  };

  return (
    <div className={cn('space-y-2', className)}>
      {showLabel && label && <Label htmlFor={id}>{label}</Label>}
      <Input
        id={id}
        name={name}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        maxLength={15}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        required={required}
        disabled={disabled}
        className={cn(
          'min-h-[44px]',
          error && 'border-red-500 focus:border-red-500',
          inputClassName,
        )}
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default WhatsAppInput;
